// src/DamageReportPage.js
import React, { useState } from 'react';

/* slug helper → "Interior Damage" → "interior-damage" */
const slugify = (str = '') => str.toLowerCase().replace(/[^a-z0-9]+/g, '-');

export default function DamageReportPage({ damageData = [] }) {
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  /* group rows by category */
  const groupedData = damageData.reduce((acc, item) => {
    const category = item.DamageCategory;
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  /* ---------- shared styles ---------- */
  const containerStyle = {
    width: '8.5in',
    minHeight: '11in',
    margin: '0 auto',
    padding: '0.3in',
    paddingBottom: '1in',          // extra bottom padding to keep logo clear
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    boxSizing: 'border-box',
    fontFamily: 'Arial, sans-serif',
    position: 'relative',
  };
  const headingStyle = {
    color: '#153BC6',
    borderBottom: '1px solid #153BC6',
    paddingBottom: '5px',
    marginBottom: '10px',
  };
  const textContainerStyle = { flex: 1 };
  const imageContainerStyle = {
    width: '100px',
    height: '100px',
    cursor: 'pointer',
    border: '1px solid #ccc',
    borderRadius: '4px',
    overflow: 'hidden',
    flexShrink: 0,
  };
  const imageStyle = { width: '100%', height: '100%', objectFit: 'cover' };

  /* ---------- modal helpers ---------- */
  function openImageModal(url) { setSelectedImageUrl(url); setZoomed(false); }
  function closeModal()         { setSelectedImageUrl(null); setZoomed(false); }
  function toggleZoom()         { setZoomed(z => !z); }

  /* ---------- early exit if no data ---------- */
  if (damageData.length === 0) {
    return (
      <div id="damage-report" style={containerStyle}>
        <h2 style={{ ...headingStyle, fontSize: '1.25rem' }}>Damage Report</h2>
        <p>No damage records found.</p>
        <div style={{ position: 'absolute', bottom: 10, right: 10 }}>
          <img src="/logo.png" alt="Company Logo" style={{ height: 40 }} />
        </div>
      </div>
    );
  }

  /* ---------- main render ---------- */
  return (
    <div id="damage-report" style={containerStyle}>
      <h2 style={{ ...headingStyle, fontSize: '1.25rem' }}>Damage Report</h2>

      {Object.entries(groupedData).map(([category, items]) => (
        <div
          key={category}
          id={`damage-${slugify(category)}`}
          style={{ marginBottom: '20px' }}
        >
          <h3 style={{ ...headingStyle, fontSize: '1.1rem' }}>{category}</h3>

          {items.map(damage => {
            const severity = damage.DamageSeverity;
            const borderColor =
              severity === 'Severe'
                ? 'red'
                : severity === 'Moderate'
                ? 'orange'
                : severity === 'Light'
                ? '#153BC6'
                : 'red';

            return (
              <div
                key={damage.DamageId}
                id={`damage-item-${damage.DamageId}`}
                style={{
                  border: `4px solid ${borderColor}`,
                  borderRadius: '8px',
                  padding: '8px',
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '10px',
                }}
              >
                <div style={textContainerStyle}>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Location:</strong> {damage.DamageLocation}
                  </p>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Severity:</strong> {severity || 'N/A'}
                  </p>
                  <p style={{ margin: '4px 0' }}>
                    <strong>Note:</strong> {damage.DamageNote || 'None'}
                  </p>
                </div>

                {damage.DamageImage && (
                  <div
                    style={imageContainerStyle}
                    onClick={() => openImageModal(damage.DamageImage)}
                  >
                    <img
                      src={damage.DamageImage}
                      alt={damage.DamageLocation}
                      style={imageStyle}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* logo */}
      <div style={{ position: 'absolute', bottom: 10, right: 10 }}>
        <img src="/logo.png" alt="Company Logo" style={{ height: 40 }} />
      </div>

      {/* full‑screen image modal */}
      {selectedImageUrl && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0, left: 0,
            width: '100%', height: '100%',
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <img
            src={selectedImageUrl}
            alt="Enlarged"
            onClick={e => { e.stopPropagation(); toggleZoom(); }}
            style={{
              maxWidth: zoomed ? '95%' : '60%',
              maxHeight: zoomed ? '95%' : '60%',
              border: '2px solid #fff',
              borderRadius: 6,
              cursor: zoomed ? 'zoom-out' : 'zoom-in',
              transition: 'all 0.3s ease',
            }}
          />
        </div>
      )}
    </div>
  );
}
